import trackerAssist from '@openreplay/tracker-assist';
import Tracker from '@openreplay/tracker';
import { create } from 'zustand';
import ApiClient from '../lib/ApiClient';
import { Room } from 'livekit-client';
import { MediaAgent, TYPES, EVENTS } from '@smarterservices/media-agent';
import config from '../config';
import { generateLinkCode } from '../helpers/helpers';
import { deviceDetect, isAndroid, isIOS, deviceType, mobileModel, mobileVendor, mobileOS } from 'react-device-detect';

/**
 * Main Application Store
 * 
 * Zustand store that manages the global application state for proctoring sessions.
 * Handles session initialization, media agent management, second camera connections,
 * API client setup, and OpenReplay tracking integration.
 * 
 * @typedef {Object} AppStore
 * @property {string|null} deviceLinkCode - Generated code for device linking
 * @property {boolean} initialized - Whether the app has been initialized
 * @property {string|null} sessionSid - Session identifier
 * @property {string|null} examSid - Exam identifier  
 * @property {string|null} installSid - Installation identifier
 * @property {string|null} token - Authentication token
 * @property {string|null} mediaToken - Media service token derived from main token
 * @property {ApiClient|null} apiClient - API client instance
 * @property {Object|null} sessionStartData - Session start configuration data
 * @property {Object} initialQueryParams - Initial URL query parameters
 * @property {string|null} sessionStatus - Current session status
 * @property {boolean} isInSessionOnboarding - Whether in onboarding flow
 * @property {boolean} isInSessionReconnection - Whether in reconnection flow
 * @property {string|null} step - Current step in the session flow
 * @property {string|null} phase - Current phase of the session
 * @property {MediaAgent|null} mediaAgent - LiveKit media agent instance
 * @property {boolean} isConnectedToRoom - Whether connected to LiveKit room
 * @property {string|null} secondCameraIdentity - Identity for second camera participant
 * @property {Participant|null} secondCameraParticipant - Second camera participant object
 * @property {TrackPublication|null} secondCameraPublication - Second camera track publication
 * @property {Object} secondCameraDeviceData - Device data from second camera
 * @property {boolean} isSecondCameraConnected - Whether second camera is connected
 * @property {string|null} mediaAgentStatus - Status of media agent creation ('creating'|'created'|null)
 * @property {string|null} type - Application type ('session'|'second-camera')
 * @property {string|null} pageErrorText - Error text to display on page
 * @property {Tracker|null} openReplayTracker - OpenReplay tracker instance
 */
const useAppStore = create((set, get) => ({
  deviceLinkCode: null,
  initialized: false,
  sessionSid: null,
  examSid: null,
  installSid: null,
  token: null,
  mediaToken: null,
  apiClient: null,
  sessionStartData: null,
  initialQueryParams: {},
  sessionStatus: null,
  isInSessionOnboarding: false,
  isInSessionReconnection: false,
  step: null,
  phase: null,
  mediaAgent: null,
  isConnectedToRoom: false,
  secondCameraIdentity: null,
  secondCameraParticipant: null,
  secondCameraPublication: null,
  secondCameraDeviceData: {},
  isSecondCameraConnected: false,
  mediaAgentStatus: null,
  type: null,
  pageErrorText: null,
  openReplayTracker: null,
  loadingText: null,


  /**
   * Initialize the application state with URL parameters or stored parameters
   *
   * @param {string} [currentRoute] - The current route path from React Router
   * @param {object} [options] - Optional parameters
   * @returns {void}
   */
  initializeApp: async (options = {}) => {

    // Set defaults for options
    const defaultOptions = {
      initialRoute: null,
      missingParamsRoute: '/missing-params',
      type: null
    };

    // Merge provided options with defaults
    options = { ...defaultOptions, ...options };

    // First check to see if the proper params are in the URL
    const url = new URL(window.location.href);
    const urlSearchParams = new URLSearchParams(url.search);
    const params = new Proxy(urlSearchParams, {
      get: (searchParams, prop) => searchParams.get(prop)
    });

    // Convert URLSearchParams to a plain object for storage
    const queryParamsObject = {};
    if (urlSearchParams) {
      for (const [key, value] of urlSearchParams.entries()) {
        queryParamsObject[key] = value;
      }
    }

    // Store the initial query params in state so we have them in the event we need to reference them later
    set({
      initialQueryParams: queryParamsObject,
      deviceLinkCode: generateLinkCode(6),
      type: options.type,
      secondCameraIdentity: `second_camera_${get().sessionSid}`,
    });

    const storedParams = get().getStoredParams();

    // If all of these are present, we are going to set them...
    if (params?.sessionSid &&
      params.examSid &&
      params.installSid &&
      params.token
    ) {
      // set the key values into state...
      set({
        sessionSid: params.sessionSid,
        examSid: params.examSid,
        installSid: params.installSid,
      });
      // set the token now...
      get().setToken(params.token);

      // Store the params in sessionStorage to be used other places
      get().setStoredParams({
        sessionSid: params.sessionSid,
        examSid: params.examSid,
        installSid: params.installSid,
        token: params.token,
      });
      // Clear the query params from the URL
      url.search = '';
      window.history.replaceState({}, document.title, url);
    } else if (storedParams) {
      // set the key values into state...
      set({
        sessionSid: storedParams.sessionSid,
        examSid: storedParams.examSid,
        installSid: storedParams.installSid,
      });
      // set the token now...
      get().setToken(storedParams.token);
    } else {
      // We do not have all the necessary params, so redirect them to a page that says that
      window.location = options.missingParamsRoute;
    }

    // Only initialize the tracker if it hasn't been initialized already
    if (!get().openReplayTracker && config.proctoring.ui.openReplay) {
      try {
        console.log('Initializing OpenReplay tracker');
        const tracker = new Tracker({
          projectKey: config.proctoring.ui.openReplay.projectKey,
          __DISABLE_SECURE_MODE: config.proctoring.ui.openReplay.disableSecureMode,
          captureIFrames: true,
          canvas: {
            enabled: true,
            disableCanvas: false,
          },
          network: {
            failuresOnly: true,
            capturePayload: true,
          },
        });

        // Only set user data if we have a session
        if (get().sessionSid) {
          tracker.setUserID(`${get().type}_${get().sessionSid}`);
          tracker.setMetadata('sessionSid', get().sessionSid);
          tracker.setMetadata('examSid', get().examSid);
          tracker.setMetadata('installSid', get().installSid);
        }

        // Initialize assist plugin
        const assistOptions = {};
        tracker.use(trackerAssist(assistOptions));

        // Start the tracker
        tracker.start();

        // Save the tracker instance
        set({ openReplayTracker: tracker });
      } catch (error) {
        console.error('Failed to initialize OpenReplay tracker:', error);
      }
    }


    // Set the api client
    get().getApiClient();

    // if the type is specified, go ahead and get the MediaAgent, which iwll load it into the store...
    if (options?.type) {
      await get().getMediaAgent();
    }

    set({ initialized: true });
  },

  /**
   * Gets or creates the API client instance
   * Creates a new ApiClient if one doesn't exist, configured with current tokens and session parameters
   * 
   * @returns {ApiClient} The API client instance
   */
  getApiClient: () => {
    if (get().apiClient) {
      return get().apiClient;
    }

    const apiClient = new ApiClient(
      {
        spToken: get().token,
        spMediaToken: get().mediaToken,
        sessionPathParams: {
          installSid: get().installSid,
          examSid: get().examSid,
          sessionSid: get().sessionSid
        }
      }
    );

    set({ apiClient });

    return apiClient;
  },

  /**
   * Fetches session start data from the API
   * Caches the result unless reload is true
   * 
   * @param {boolean} [reload=false] - Whether to force reload the data
   * @returns {Promise<Object|null>} Session start data or null on error
   */
  getSessionStartData: async (reload = false) => {
    if (reload || !get().sessionStartData) {
      try {
        const sessionStartData = await get().apiClient.getSessionStart();
        set({ sessionStartData });
      } catch (error) {
        console.error('Error fetching session start data:', error);
        return null;
      }
    }
    return get().sessionStartData;
  },

  // State setters
  /** @param {string} sessionSid - Session identifier */
  setSessionSid: (sessionSid) => set({ sessionSid }),
  /** @param {string} examSid - Exam identifier */
  setExamSid: (examSid) => set({ examSid }),
  /** @param {string} installSid - Installation identifier */
  setInstallSid: (installSid) => set({ installSid }),
  /** @param {string} sessionStatus - Current session status */
  setSessionStatus: (sessionStatus) => set({ sessionStatus }),
  /** @param {string} step - Current step in the session flow */
  setStep: (step) => set({ step }),
  /** @param {string} phase - Current phase of the session */
  setPhase: (phase) => set({ phase }),
  /** @param {boolean} isInSessionOnboarding - Whether in onboarding flow */
  setIsInSessionOnboarding: (isInSessionOnboarding) => set({ isInSessionOnboarding }),
  /** @param {boolean} isInSessionReconnection - Whether in reconnection flow */
  setIsInSessionReconnection: (isInSessionReconnection) => set({ isInSessionReconnection }),
  /** @param {boolean} isConnectedToRoom - Whether connected to LiveKit room */
  setIsConnectedToRoom: (isConnectedToRoom) => set({ isConnectedToRoom }),
  /** @param {string} pageErrorText - Error text to display on page */
  setPageErrorText: (pageErrorText) => set({ pageErrorText }),
  /** Clears the page error text */
  clearPageErrorText: () => set({ pageErrorText: null }),
  setLoadingText: (loadingText) => set({ loadingText }),
  clearLoadingText: () => set({ loadingText: null }),

  /**
   * Restores launch parameters to the URL from session storage
   * Used to restore URL parameters after they've been cleared
   */
  restoreLaunchParams: () => {
    try {
      const params = get().getStoredParams();

      const url = new URL(window.location.href);
      url.search = new URLSearchParams(params || {}).toString();
      window.history.replaceState(null, '', url); // no reload, just update URL
    } catch (error) {
      console.error('Error restoring the query parameters:', error);
      return null;
    }
  },
  /**
   * Removes launch parameters from the URL
   * Clears query parameters without reloading the page
   */
  removeLaunchParams: () => {
    try {
      const url = new URL(window.location.href);
      url.search = '';
      window.history.replaceState(null, '', url); // no reload, just update URL
    } catch (error) {
      console.error('Error removing the query parameters:', error);
      return null;
    }
  },
  /**
   * Retrieves stored session parameters from sessionStorage
   * 
   * @returns {Object|null} Parsed session parameters or null if not found/invalid
   */
  getStoredParams: () => {
    try {
      const params = sessionStorage.getItem('spParams');
      return params ? JSON.parse(params) : null;
    } catch (error) {
      console.error('Error parsing session parameters:', error);
      return null;
    }
  },
  /**
   * Stores session parameters in sessionStorage
   * 
   * @param {Object} params - Session parameters to store
   * @param {string} params.sessionSid - Session identifier
   * @param {string} params.examSid - Exam identifier
   * @param {string} params.installSid - Installation identifier
   * @param {string} params.token - Authentication token
   */
  setStoredParams: (params) => {
    try {
      sessionStorage.setItem('spParams', JSON.stringify(params));
    } catch (error) {
      console.error('Error parsing session parameters:', error);
      return null;
    }
  },
  /**
   * Sets the authentication token and derives the media token
   * Validates JWT format and extracts the signature for media token generation
   * 
   * @param {string} token - JWT authentication token
   */
  setToken: (token) => {
    const updateObj = { token };

    // Only process the token if it's provided
    if (token) {
      // Validate that the token is in the expected JWT format (three parts separated by dots)
      const tokenParts = token.split('.');

      if (tokenParts.length === 3) {
        // Valid JWT format with three segments
        const shortenedToken = tokenParts[2];
        updateObj.mediaToken = `tok_${shortenedToken}`;
      } else {
        // Invalid token format - log warning and set a default media token
        console.warn('Invalid token format: expected JWT with three segments');
        // Don't update the mediaToken or set a fallback if needed
      }
    }

    set(updateObj);
  },

  /**
   * Gets or creates the MediaAgent instance for LiveKit communication
   * Handles race conditions with status flags and manages room connection
   * Sets up event listeners for second camera participant management
   * 
   * @returns {Promise<MediaAgent>} The media agent instance
   * @throws {Error} When media agent creation fails or type is not set
   */
  getMediaAgent: async () => {

    let mediaAgent;

    console.log('Get Media Agent....');

    // Race condition protection: wait if another instance is already creating the agent
    if (get().mediaAgentStatus === 'creating') {
      while (get().mediaAgentStatus === 'creating') {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      if (get().mediaAgentStatus === 'created' && get().mediaAgent) {
        return get().mediaAgent;
      } else {
        // Creation failed in another instance
        throw new Error('Media agent not found in store. Cannot get media agent.');
      }
    }

    if (!get().mediaAgent) {
      // Set creation flag to prevent race conditions
      set({ mediaAgentStatus: 'creating' });
      console.log('Media agent not found in store. Creating...');
      let identity;
      let type;

      // Determine participant identity and type based on application mode
      if (get().type === "second-camera") {
        identity = `second_camera_${get().sessionSid}`;
        type = TYPES.SECOND_CAMERA;
      } else if (get().type === "session") {
        identity = `session_${get().sessionSid}`;
        type = TYPES.STUDENT;
      } else {
        throw new Error('No type set in store. Cannot create media agent.');
      }

      // Collect comprehensive device information for participant metadata
      const deviceData = {
        ...deviceDetect(),
        isAndroid: isAndroid,
        isIOS: isIOS,
        deviceType: deviceType,
        mobileModel: mobileModel,
        mobileVendor: mobileVendor,
        mobileOS: mobileOS,
      };

      const agentConfig = {
        apiUrl: config.proctoring.api.baseUrl,
        roomId: get().sessionSid,
        identity,
        token: get().token,
        websocketUrl: config.media.livekit.websocketUrl,
        type,
        participant: {
          examSid: get().examSid,
          installSid: get().installSid,
          sessionSid: get().sessionSid,
          metadata: {
            deviceData,
          },
        },
      };

      mediaAgent = await MediaAgent(agentConfig);

      set({ mediaAgent });

      // now join the room...
      await mediaAgent.joinRoom();

      // set the variables that tracks if we are connected to the room...
      get().setIsConnectedToRoom(true);

      // Second camera participant management (only for main session, not second camera itself)
      if (get().type !== "second-camera") {


        // Room-level trackPublished handler - DO NOT subscribe here, let participant handlers do it
        mediaAgent.getRoom().on('trackPublished', async (publication) => {
          console.log('[ROOM] Track published:', publication.trackName, 'isSubscribed:', publication.isSubscribed);
          // Don't subscribe here - participant-level handlers will handle it
        });

        // Check for existing second camera participants on room join
        // Use for...of instead of forEach to properly handle async operations
        console.log('[INIT] Checking for existing second camera participants...');
        console.log('[INIT] Remote participants:', Array.from(mediaAgent.getRoom().remoteParticipants.keys()));
        console.log('[INIT] Looking for identity:', get().secondCameraIdentity);

        for (const participant of mediaAgent.getRoom().remoteParticipants.values()) {
          if (participant.identity === get().secondCameraIdentity) {
            console.log('[INIT] Found existing second camera participant:', participant.identity);
            console.log('[INIT] Participant video publications:', Array.from(participant.videoTrackPublications.keys()));

            // Set up event listeners FIRST before any subscription attempts
            participant.on('trackSubscribed', (track, publication) => {
              console.log('[EVENT] trackSubscribed fired:', publication.trackName, track);
              if (publication?.trackName === 'secondCamera') {
                console.log('[EVENT] Second camera track subscribed (existing participant):', publication);
                set({ secondCameraPublication: publication, isSecondCameraConnected: true });
              }
            });

            participant.on('trackUnsubscribed', async (track, publication) => {
              console.log('[EVENT] trackUnsubscribed fired:', publication.trackName);
              if (publication?.trackName === 'secondCamera') {
                await get().setSecondCameraParticipant(null);
              }
            });

            participant.on('participantDisconnected', async (participant) => {
              if (participant.identity === get().secondCameraIdentity) {
                console.log('[EVENT] Participant disconnected:', participant);
              }
            });

            participant.on('participantMetadataChanged', async () => {
              console.log('[EVENT] Participant metadata changed');
              if (participant?.attributes?.type === 'second-camera') {
                await get().setSecondCameraParticipant(participant);
              }
            });

            await get().setSecondCameraParticipant(participant);

            // NOW try to subscribe - handlers are ready
            const publication = participant.getTrackPublicationByName('secondCamera');
            console.log('[INIT] Found publication:', publication?.trackName, 'isSubscribed:', publication?.isSubscribed);

            if (publication) {
              if (!publication.isSubscribed) {
                console.log('[INIT] Explicitly subscribing to second camera track (existing participant)');
                publication.setSubscribed(true);
              } else {
                console.log('[INIT] Track already subscribed, setting state');
                set({ secondCameraPublication: publication, isSecondCameraConnected: true });
              }
            } else {
              console.log('[INIT] No secondCamera publication found yet');
            }
          }
        }

        // Register room-level event listeners for new participant connections
        mediaAgent.getRoom().on('participantConnected', async (participant) => {
          console.log('[ROOM] Participant connected:', participant.identity);
          console.log('[ROOM] Participant metadata:', participant.metadata);
          console.log('[ROOM] Participant attributes:', participant.attributes);
          console.log('[ROOM] Participant audio tracks:', Array.from(participant.audioTrackPublications.keys()));
          console.log('[ROOM] Participant video tracks:', Array.from(participant.videoTrackPublications.keys()));
          if (participant.identity === get().secondCameraIdentity) {
            console.log('[NEW PARTICIPANT] Second camera participant connected:', participant.identity);

            // Set up track subscription listeners for new participant
            participant.on('trackSubscribed', (track, publication) => {
              console.log('[NEW PARTICIPANT EVENT] trackSubscribed:', publication.trackName, track);
              if (publication?.trackName === 'secondCamera') {
                console.log('[NEW PARTICIPANT EVENT] Second camera track subscribed');
                set({ secondCameraPublication: publication, isSecondCameraConnected: true });
              }
            });

            // Handle track published event - explicitly subscribe to the track
            participant.on('trackPublished', (publication) => {
              console.log('[NEW PARTICIPANT EVENT] trackPublished:', publication.trackName, 'isSubscribed:', publication.isSubscribed);
              if (publication?.trackName === 'secondCamera' && !publication.isSubscribed) {
                console.log('[NEW PARTICIPANT EVENT] Explicitly subscribing to second camera track');
                publication.setSubscribed(true);
              }
            });

            participant.on('trackUnsubscribed', async (track, publication) => {
              console.log('[NEW PARTICIPANT EVENT] trackUnsubscribed:', publication.trackName);
              if (publication?.trackName === 'secondCamera') {
                await get().setSecondCameraParticipant(null);
              }
            });

            participant.on('trackUnpublished', async (track, publication) => {
              console.log('[NEW PARTICIPANT EVENT] trackUnpublished:', publication.trackName);
            });

            participant.on('connectionQualityChanged', async (change) => {
              console.log('[NEW PARTICIPANT EVENT] connectionQualityChanged:', change);
            });

            participant.on('participantMetadataChanged', async () => {
              console.log('[NEW PARTICIPANT EVENT] participantMetadataChanged');
              if (participant?.attributes?.type === 'second-camera') {
                await get().setSecondCameraParticipant(participant);
              }
            });

            // Store the new participant in the app state
            await get().setSecondCameraParticipant(participant);
          }
        });
        // Handle second camera participant disconnection
        mediaAgent.getRoom().on('participantDisconnected', async (participant) => {
          console.log('[ROOM] Participant disconnected:', participant.identity);
          if (participant.identity === get().secondCameraIdentity) {
            console.log('[ROOM] Second camera participant disconnected');
            await get().setSecondCameraParticipant(null);
          }
        });
      }

      // Set up MediaAgent event listeners for connection and track management
      const eventEmitter = mediaAgent.getEventEmitter();

      eventEmitter.on(EVENTS.CONNECTED_TO_ROOM, (data) => {
        console.log(`Room connected:`, data);
      });

      eventEmitter.on(EVENTS.DISCONNECTED_FROM_ROOM, (data) => {
        get().setIsConnectedToRoom(false);
        console.log(`Room disconnected:`, data);
      });

      // Listen for media track lifecycle events
      eventEmitter.on(EVENTS.MEDIA_TRACK_STARTED, (data) => {
        console.log(`Track started: ${data.trackName}`);
      });

      eventEmitter.on(EVENTS.MEDIA_TRACK_ENDED, (data) => {
        console.log(`Track ended: ${data.trackName}`);
      });

      set({
        mediaAgentStatus: 'created'
      });

    } else {
      // Return existing media agent instance
      console.log('Media agent found in store.');
      mediaAgent = get().mediaAgent;
    }

    return mediaAgent;
  },

  /**
   * Initializes the app using a stashed device link code
   * Used by second camera devices to authenticate and join sessions
   * 
   * @param {Object} params - Initialization parameters
   * @param {string} params.code - Device link code to validate
   * @param {string} params.type - Application type ('second-camera')
   * @returns {Promise<void>} Resolves when initialization is complete
   * @throws {Error} When code is invalid or initialization fails
   */
  initializeAppFromStash: async ({ code, type }) => {
    const apiClient = new ApiClient();
    // Validate the device link code and retrieve session parameters
    try {
      const stashData = await apiClient.getStash(code, false);
      if (!stashData) {
        throw new Error('Invalid code');
      }

      const { sessionSid, examSid, installSid, token } = stashData;

      // Store retrieved parameters for session initialization
      get().setStoredParams({
        sessionSid,
        examSid,
        installSid,
        token,
      });

      return await get().initializeApp({ type });

    } catch (error) {
      // Handle invalid code error with user-friendly message
      if (error.response.data.code === '1578') {
        get().setPageErrorText(`Device link code ${code} is invalid. Please confirm the device link code and try again.<br/><br/>
          <a href="/session/second-camera">Start Over</a>`);
      }
      throw error;
    }
  },

  /**
   * Extracts device data from second camera participant metadata
   * Parses JSON metadata to retrieve device information
   * 
   * @returns {Object} Device data object or empty object if not available
   */
  getSecondCameraParticipantDeviceData: () => {
    let deviceData = {};
    // Extract device info from participant metadata
    if (get().secondCameraParticipant?.metadata) {
      try {
        const metadata = JSON.parse(get().secondCameraParticipant.metadata);
        deviceData = metadata?.deviceData;
        return deviceData;
      } catch (error) {
        console.log('Error getting device data:', error);
      }
    }
    return deviceData;
  },

  /**
   * Sets the second camera participant and retrieves device data
   * Handles both metadata extraction and RPC calls for comprehensive device info
   * 
   * @param {Participant|null} secondCameraParticipant - The participant object or null to clear
   */
  setSecondCameraParticipant: async (secondCameraParticipant) => {

    if (secondCameraParticipant) {
      let deviceData = null;

      // Extract initial device data from participant metadata
      if (secondCameraParticipant?.metadata) {
        try {
          const metadata = JSON.parse(secondCameraParticipant.metadata);
          deviceData = metadata?.deviceData?.deviceInfo;
        } catch (error) {
          console.log('Error getting device data:', error);
        }
        console.log(deviceData);
      }

      // Set participant and initial device data
      set({ secondCameraParticipant, secondCameraDeviceData: deviceData });

      // Fetch additional device data via RPC if connected
      if (get().isSecondCameraConnected) {
        try {
          console.log(secondCameraParticipant);

          // Request comprehensive device data from second camera
          const deviceDataResponse = await get().mediaAgent.performRpc({
            destinationIdentity: secondCameraParticipant.identity,
            method: 'getDeviceData',
            payload: {},
          });

          const responseJson = JSON.parse(deviceDataResponse);

          if (responseJson.successful) {
            deviceData = responseJson.data;
          }
          set({ secondCameraDeviceData: deviceData });
        } catch (error) {
          console.error('Error getting device data:', error);
        }
      }

    } else {
      // Clear all second camera related state
      set({ secondCameraParticipant: null, isSecondCameraConnected: false, secondCameraDeviceData: null, secondCameraPublication: null });
    }
  },

}));

export default useAppStore;
export { useAppStore };
